import { ReactElement } from 'react'
import { useController, useWatch } from 'react-hook-form'
import { find, isUndefined, isNull } from 'lodash-es'
import {
  Select as MuiSelect,
  MenuItem,
  Typography,
  BaseSelectProps,
  InputLabel,
} from '@mui/material'
import FormControl from '../FormControl'

interface Props<F> extends BaseSelectProps {
  name: F extends object ? Extract<keyof F, string> : string
  options: {
    value: string | number
    label: string
    disabled?: boolean
  }[]
  placeholder?: string
  label?: string | ReactElement
  inputLabel?: string
  disabled?: boolean
  tooltip?: TooltipContents
  'data-cy'?: string
}

const Select = <F,>({
  name,
  options,
  placeholder = '',
  label = '',
  inputLabel,
  disabled = false,
  tooltip,
  'data-cy': dataCy,
  ...props
}: Props<F>): ReactElement => {
  const {
    field: { onChange, onBlur, name: fieldName, ref },
    fieldState: { error },
    // formState: { touchedFields, dirtyFields }
  } = useController({
    name,
  })

  const value = useWatch({ name })

  const hasValue = !isNull(value) && !isUndefined(value)

  return (
    <FormControl label={inputLabel ? undefined : label} error={error?.message} tooltip={tooltip}>
      {inputLabel && (
        <InputLabel size="small" shrink={hasValue || Boolean(placeholder)}>
          {inputLabel}
        </InputLabel>
      )}
      <MuiSelect
        onChange={onChange}
        onBlur={onBlur}
        value={isNull(value) ? '' : value}
        name={fieldName}
        inputRef={ref}
        size="small"
        disabled={disabled}
        displayEmpty
        label={inputLabel}
        data-cy={`${dataCy}-dropdown`}
        renderValue={value => {
          if (isNull(value) || isUndefined(value)) {
            return (
              <Typography
                sx={({ palette }) => ({
                  color: palette.grey[500],
                })}
              >
                {placeholder}
              </Typography>
            )
          }

          return find(options, ['value', value])?.label
        }}
        {...props}
      >
        {options.map(({ value, label, disabled: optionDisabled }, index: number) => (
          <MenuItem
            key={value.toString() + index}
            value={value}
            disabled={optionDisabled}
            data-cy={`${dataCy}-item-${index}`}
          >
            {label}
          </MenuItem>
        ))}
      </MuiSelect>
    </FormControl>
  )
}

export default Select
